import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, attachToken, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

const TOKEN_KEY = "misco_token";

let msalPromise;
function getMsal() {
  if (!msalPromise) {
    msalPromise = (async () => {
      const { data: cfg } = await api.get("/auth/sso-config");
      if (!cfg.enabled) throw new Error("Microsoft sign-in is not configured yet");
      const { PublicClientApplication } = await import("@azure/msal-browser");
      const msal = new PublicClientApplication({
        auth: { clientId: cfg.client_id, authority: `https://login.microsoftonline.com/${cfg.tenant_id}`, redirectUri: window.location.origin + "/login", navigateToLoginRequestUrl: false },
        cache: { cacheLocation: "sessionStorage" },
      });
      await msal.initialize();
      return msal;
    })();
  }
  return msalPromise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading; false = anon
  const [error, setError] = useState("");

  const exchangeEntraToken = async (idToken) => {
    let data;
    for (let attempt = 0; ; attempt++) {
      try {
        ({ data } = await api.post("/auth/entra", {}, { headers: { Authorization: `Bearer ${idToken}` } }));
        break;
      } catch (e) {
        if (e.response || attempt >= 4) throw e;
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    attachToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const bootstrap = useCallback(async () => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) attachToken(saved);
    if (window.location.hash.includes("code=") || window.location.hash.includes("id_token=") || window.location.hash.includes("error=")) {
      try {
        const msal = await getMsal();
        const result = await msal.handleRedirectPromise();
        if (result?.idToken) { await exchangeEntraToken(result.idToken); return; }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.errorMessage || (e.response ? e.message : "Couldn't reach the server while finishing Microsoft sign-in. Please click Sign in with Microsoft again."));
        setUser(false);
        return;
      }
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      setUser(false);
      attachToken(null);
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        attachToken(data.token);
      }
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        attachToken(data.token);
      }
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const loginWithMicrosoft = async () => {
    setError("");
    if (window.self !== window.top) {
      window.open(window.location.origin + "/login", "_blank", "noopener");
      throw new Error("Microsoft sign-in can't run inside an embedded preview — we opened the app in a new tab. Please continue there.");
    }
    const msal = await getMsal();
    await msal.loginRedirect({ scopes: ["openid", "profile", "email"], prompt: "select_account", redirectUri: window.location.origin + "/login" });
    return new Promise(() => {});
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { console.error("Logout request failed", e); }
    localStorage.removeItem(TOKEN_KEY);
    attachToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, register, logout, setUser, loginWithMicrosoft }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
